/**
 * API client for ACP backend.
 * Handles auth tokens, refresh, and request/response interceptors.
 */

const API_BASE = "/api";

interface ApiOptions extends RequestInit {
  json?: any;
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Restore tokens from localStorage
    this.accessToken = localStorage.getItem("acp_access_token");
    this.refreshToken = localStorage.getItem("acp_refresh_token");
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access;
    this.refreshToken = refresh;
    localStorage.setItem("acp_access_token", access);
    localStorage.setItem("acp_refresh_token", refresh);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem("acp_access_token");
    localStorage.removeItem("acp_refresh_token");
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  private async request<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
    const { json, ...init } = opts;

    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    if (json !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(json);
    }

    let res = await fetch(`${API_BASE}${path}`, { ...init, headers });

    // If 401, try refresh
    if (res.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${this.accessToken}`;
        res = await fetch(`${API_BASE}${path}`, { ...init, headers });
      }
    }

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || `API error: ${res.status}`);
    }

    if (res.status === 204) return undefined as T;

    return res.json();
  }

  private async tryRefresh(): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!res.ok) {
        this.clearTokens();
        return false;
      }

      const data = await res.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  // ── Auth ──
  async login(username: string, password: string) {
    const data = await this.request("/auth/login", {
      method: "POST",
      json: { username, password },
    });
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async register(username: string, password: string) {
    const data = await this.request("/auth/register", {
      method: "POST",
      json: { username, password },
    });
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async getMe() {
    return this.request("/auth/me");
  }

  logout() {
    this.clearTokens();
  }

  /** Authenticated fetch — returns raw Response for SSE/streaming */
  async fetchAuth(path: string, init: RequestInit = {}): Promise<Response> {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string>),
    };
    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    let res = await fetch(`${API_BASE}${path}`, { ...init, headers });

    if (res.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${this.accessToken}`;
        res = await fetch(`${API_BASE}${path}`, { ...init, headers });
      }
    }

    return res;
  }

  // ── Projects ──
  async getProjects() {
    return this.request("/projects");
  }

  async createProject(name: string, description?: string) {
    return this.request("/projects", {
      method: "POST",
      json: { name, description },
    });
  }

  async getProject(id: string) {
    return this.request(`/projects/${id}`);
  }

  async updateProject(id: string, data: { name?: string; description?: string }) {
    return this.request(`/projects/${id}`, {
      method: "PATCH",
      json: data,
    });
  }

  async deleteProject(id: string) {
    return this.request(`/projects/${id}`, { method: "DELETE" });
  }

  // ── Chat ──
  async sendMessage(projectId: string, content: string): Promise<EventSource> {
    // SSE requires special handling — return EventSource for streaming
    const es = new EventSource(
      `${API_BASE}/chat?projectId=${projectId}&content=${encodeURIComponent(content)}`
    );
    return es;
  }

  async sendMessagePost(projectId: string, content: string) {
    // Fallback: POST with streaming response
    return this.request("/chat", {
      method: "POST",
      json: { projectId, content },
    });
  }

  async getChatHistory(projectId: string, limit = 50) {
    return this.request(`/chat/${projectId}/history?limit=${limit}`);
  }

  // ── Files ──
  async uploadFile(projectId: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);

    const headers: Record<string, string> = {};
    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    const res = await fetch(`${API_BASE}/files/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || `Upload failed: ${res.status}`);
    }

    return res.json();
  }

  async getFiles(projectId: string) {
    return this.request(`/files/${projectId}`);
  }

  async deleteFile(id: string) {
    return this.request(`/files/${id}`, { method: "DELETE" });
  }

  // ── Cron ──
  async getCronJobs() {
    return this.request("/cron/jobs");
  }

  async runCronJob(jobId: string) {
    return this.request(`/cron/jobs/${jobId}/run`, { method: "POST" });
  }

  async getCronRuns(jobId: string) {
    return this.request(`/cron/jobs/${jobId}/runs`);
  }

  // ── Notifications ──
  async getNotifications() {
    return this.request("/notifications");
  }

  async markNotificationRead(id: string) {
    return this.request(`/notifications/${id}`, { method: "PATCH" });
  }

  async markAllNotificationsRead() {
    return this.request("/notifications/read-all", { method: "POST" });
  }

  // ── Health ──
  async getHealth() {
    return this.request("/health");
  }
}

export const api = new ApiClient();
